import { Static, Type } from '@sinclair/typebox';
import { SeoActivityContextSchema } from '../activity/activity.schema';
import { AddressSchema } from '../professional';
import { SeoScope } from '../translation_namespace/seo_scope_namespace';
import { MAX_SERVICE_AREAS, ServiceAreasSchema, ServiceAreaSchema, ServiceAreaTypeSchema } from './service-area.schema';

/**
 * Schémas request/response des endpoints `/app/professional/seo-scope`
 * et `/app/professional/service-areas`.
 *
 * Voir docs/to_implement/website-locations-pages-plan.md.
 */

/** Sentinelle texte obligatoire dans le body pour confirmer la bascule LOCAL → NATIONAL. */
export const SCOPE_TRANSITION_CONFIRMATION_TEXT = 'JE CONFIRME';

export const PatchSeoScopeRequestSchema = Type.Object({
  scope: Type.Enum(SeoScope),
  /**
   * Doit valoir exactement `SCOPE_TRANSITION_CONFIRMATION_TEXT` ('JE CONFIRME')
   * pour la transition LOCAL → NATIONAL. Ignoré pour NATIONAL → LOCAL.
   * Côté backend, throw `SCOPE_TRANSITION_REQUIRES_CONFIRMATION` si la
   * transition est destructive et la chaîne est absente / mal saisie.
   */
  confirmation: Type.Optional(Type.String({ maxLength: 32 })),
});
export type PatchSeoScopeRequestObject = Static<typeof PatchSeoScopeRequestSchema>;

export const SeoScopeResponseSchema = Type.Object({
  scope: Type.Enum(SeoScope),
  service_areas: ServiceAreasSchema,
});
export type SeoScopeResponseObject = Static<typeof SeoScopeResponseSchema>;

/**
 * POST /app/professional/service-areas — payload natif renvoyé par
 * `AddressAutocomplete mode="areas"` (un `AddressObject` complet + le type
 * de zone sélectionné). Le service backend mappe vers ServiceArea, calcule
 * `area_key` (avec préfixe selon type) et `url_slug` (du label).
 * **Pas de `place_id` persisté** (provider-agnostique).
 */
export const AddServiceAreaRequestSchema = Type.Object({
  address: AddressSchema,
  area_type: ServiceAreaTypeSchema,
});
export type AddServiceAreaRequestObject = Static<typeof AddServiceAreaRequestSchema>;

/**
 * PUT /app/professional/service-areas — payload atomique qui remplace
 * **complètement** le scope SEO + la liste des zones. Utilisé par le panneau
 * Settings SEO du builder qui travaille en draft local et persiste tout au
 * clic Save global (parité avec les onglets Branding + Tracking).
 *
 * Le backend (re)calcule `area_key`, `url_slug`, `display_order` (séquentiel)
 * et `is_primary` (idx 0 = primary). Les autres champs proviennent de
 * l'AddressObject Google Places.
 */
export const ReplaceServiceAreasItemSchema = Type.Object({
  area_type: ServiceAreaTypeSchema,
  name: Type.String({ minLength: 1, maxLength: 120 }),
  formatted_address: Type.String({ minLength: 1, maxLength: 300 }),
  country_code: Type.String({ pattern: '^[A-Z]{2}$' }),
  admin_area: Type.Optional(Type.String({ maxLength: 120 })),
  postal_code: Type.Optional(Type.String({ maxLength: 20 })),
  lat: Type.Optional(Type.Number({ minimum: -90, maximum: 90 })),
  lng: Type.Optional(Type.Number({ minimum: -180, maximum: 180 })),
});
export type ReplaceServiceAreasItemObject = Static<typeof ReplaceServiceAreasItemSchema>;

export const ReplaceServiceAreasRequestSchema = Type.Object({
  scope: Type.Enum(SeoScope),
  /**
   * Doit valoir exactement `SCOPE_TRANSITION_CONFIRMATION_TEXT` ('JE CONFIRME')
   * pour la transition LOCAL → NATIONAL destructive. Ignoré sinon.
   */
  confirmation: Type.Optional(Type.String({ maxLength: 32 })),
  /**
   * Liste cible des zones (max `MAX_SERVICE_AREAS`). L'ordre du tableau pilote
   * `display_order` + `is_primary` (idx 0 = primary). Tableau vide = aucune
   * zone (peut être légitime en NATIONAL).
   */
  areas: Type.Array(ReplaceServiceAreasItemSchema, { maxItems: MAX_SERVICE_AREAS }),
});
export type ReplaceServiceAreasRequestObject = Static<typeof ReplaceServiceAreasRequestSchema>;

export const ReorderServiceAreasRequestSchema = Type.Object({
  /** Liste de `area_key` dans l'ordre souhaité. Doit contenir TOUTES les zones actuelles. */
  order: Type.Array(Type.String({ pattern: '^[a-z0-9-]+:[a-z]{2}$' }), {
    minItems: 1,
    maxItems: MAX_SERVICE_AREAS,
  }),
});
export type ReorderServiceAreasRequestObject = Static<typeof ReorderServiceAreasRequestSchema>;

export const UpdateServiceAreaRequestSchema = Type.Object({
  is_primary: Type.Optional(Type.Boolean()),
});
export type UpdateServiceAreaRequestObject = Static<typeof UpdateServiceAreaRequestSchema>;

/** Réponse alias — un seul ServiceArea pour les endpoints unitaires (POST, PATCH). */
export const ServiceAreaResponseSchema = ServiceAreaSchema;

/**
 * Contexte SEO scope-aware produit par `SeoScopeBuilder` côté backend
 * et consommé par `custom-websites` (templates, JSON-LD, sitemap).
 *
 * Présence des champs selon le scope :
 *   - LOCAL    → primary_area, primary_area_postal, service_areas_inline,
 *                service_areas_top3 présents.
 *                country_code reste utile pour disambiguation.
 *   - NATIONAL → country_code présent (custom-websites résoudra le national_label
 *                via le namespace `COUNTRIES`). Les champs LOCAL sont absents.
 *
 * `json_ld_address` est **toujours** dérivé du `pro_place.is_default` — peut être
 * `null` si le pro n'a aucun lieu physique (100% à domicile / visio). Indépendant
 * du scope (cf. plan D-12 : pro_place ⊥ service_areas).
 */
export const SeoScopeContextSchema = Type.Object({
  scope: Type.Enum(SeoScope),

  // Présents UNIQUEMENT en LOCAL — dérivés de service_areas[].is_primary
  primary_area: Type.Optional(Type.String()),
  primary_area_postal: Type.Optional(Type.String()),
  service_areas_inline: Type.Optional(Type.String()),
  service_areas_top3: Type.Optional(Type.Array(Type.String(), { maxItems: 3 })),
  /**
   * Suffixe pré-formaté à ajouter au `<title>` quand `scope=LOCAL` — ex : `" à Lyon"`.
   * Calculé côté backend. Vide en NATIONAL ou si aucune primary_area.
   */
  title_local_suffix: Type.Optional(Type.String({ maxLength: 80 })),

  // Présent en NATIONAL — code ISO 3166-1 alpha-2 du pays du pro (ex: "FR").
  country_code: Type.Optional(Type.String({ pattern: '^[A-Z]{2}$' })),

  // Adresse JSON-LD — provient de `pro_place.is_default === true` si existe, sinon null.
  // Indépendant du primary_area (cf. plan D-12).
  json_ld_address: Type.Optional(AddressSchema),

  /**
   * Contexte activités résolu (primary_display, primary_with_article, schema_org_types, …).
   * Présent dès que le pro a ≥1 `professional_activity`.
   */
  activity_context: Type.Optional(SeoActivityContextSchema),
});
export type SeoScopeContextObject = Static<typeof SeoScopeContextSchema>;
